import {
  createContext,
  use,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

type BreadcrumbContextProps = {
  /** Le dernier maillon, quand un écran en fournit un. */
  leaf: string | null;
  setLeaf: (_leaf: string | null) => void;
};

const BreadcrumbContext = createContext<BreadcrumbContextProps | null>(null);

/**
 * Le maillon final du fil d'Ariane, posé par l'écran qui le connaît.
 *
 * ⚠️ **Pourquoi un contexte plutôt qu'une déduction depuis l'URL.** Les
 * sections se déduisent très bien de `NAVIGATION` — mais « Comptes > nolan.dice »
 * ne s'écrit qu'avec une donnée chargée. L'URL ne porte qu'un identifiant, et
 * l'en-tête n'a aucun moyen de le résoudre : il faudrait qu'il refasse la
 * requête de l'écran, ou que chaque écran redessine son propre fil.
 *
 * Le contexte est vide par défaut : un écran qui ne dit rien affiche sa section,
 * et rien de plus.
 */
export function BreadcrumbProvider({ children }: PropsWithChildren) {
  const [leaf, setLeaf] = useState<string | null>(null);

  const value = useMemo(() => ({ leaf, setLeaf }), [leaf]);

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

/** Lu par l'en-tête. `null` hors de la coque — le fil n'y existe pas. */
export function useBreadcrumbLeaf(): string | null {
  return use(BreadcrumbContext)?.leaf ?? null;
}

/**
 * Déclare le dernier maillon depuis un écran.
 *
 * ⚠️ **Le nettoyage au démontage n'est pas optionnel.** Sans lui, le pseudo du
 * compte qu'on vient de quitter resterait affiché sur l'écran suivant — un fil
 * d'Ariane qui ment sur l'endroit où l'on se trouve est pire que pas de fil du
 * tout.
 *
 * `undefined` pendant le chargement : la section s'affiche seule, et le maillon
 * apparaît quand la donnée arrive.
 */
export function useSetBreadcrumbLeaf(leaf: string | null | undefined) {
  const context = use(BreadcrumbContext);
  const setLeaf = context?.setLeaf;

  useEffect(() => {
    if (!setLeaf) return;

    setLeaf(leaf ?? null);
    return () => setLeaf(null);
  }, [leaf, setLeaf]);
}
